// packages/web/src/components/AdminQuotes.tsx
import React, { useState, useEffect } from 'react'
import { Link } from '@tanstack/react-router'
import {
  FileText,
  Plus,
  Send,
  ArrowRight,
  CheckCircle,
  Clock,
  Search,
} from 'lucide-react'
import { apiGetWithMeta, apiPost } from '../lib/api'
import type { Quote } from '@buddys-choice/shared'
import { DataTablePagination } from './DataTablePagination'
import { AppDataTable } from './AppDataTable'

export const AdminQuotes: React.FC = () => {
  const [quotes, setQuotes] = useState<Quote[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const pageSize = 10

  useEffect(() => {
    fetchQuotes()
  }, [search, page])

  const fetchQuotes = () => {
    setLoading(true)
    apiGetWithMeta<Quote[]>('quotes', { search: search || undefined, page, pageSize })
      .then((res) => {
        setQuotes(res.data || [])
        setTotal(res.meta?.total ?? 0)
        setTotalPages(res.meta?.totalPages ?? 1)
      })
      .catch((err) => console.error('Fetch quotes error:', err))
      .finally(() => setLoading(false))
  }

  const handleSendQuote = async (id: string) => {
    try {
      await apiPost(`quotes/${id}/send`, { validDays: 7 })
      fetchQuotes()
      alert('Quote sent to customer with 7 days validity window!')
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to send quote.')
    }
  }

  const handleConvertQuote = async (id: string) => {
    try {
      await apiPost(`quotes/${id}/convert-to-order`, {})
      fetchQuotes()
      alert('Quote converted to active order successfully!')
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to convert quote.')
    }
  }

  const formatCurrency = (amt: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt)

  const quoteColumns = [
    {
      name: 'Quote No',
      sortable: true,
      sortValue: (q: Quote) => q.quoteNumber,
      selector: (q: Quote) => (
        <span className="font-mono font-bold text-[var(--gold-dark)]">{q.quoteNumber}</span>
      ),
    },
    {
      name: 'Customer',
      selector: (q: Quote) => q.customerName,
    },
    {
      name: 'Phone',
      selector: (q: Quote) => (
        <span className="font-mono text-[var(--text-primary)]/70">{q.customerPhone}</span>
      ),
    },
    {
      name: 'Status',
      cell: (q: Quote) => <span className={`badge badge-${q.status}`}>{q.status}</span>,
    },
    {
      name: 'Total Value',
      sortable: true,
      sortValue: (q: Quote) => q.total,
      selector: (q: Quote) => (
        <span className="font-serif font-bold">{formatCurrency(q.total)}</span>
      ),
    },
    {
      name: 'Date',
      selector: (q: Quote) => (
        <span className="text-xs text-[var(--text-primary)]/50">
          {new Date(q.createdAt).toLocaleDateString('en-IN')}
        </span>
      ),
    },
    {
      name: 'Actions',
      cell: (q: Quote) => (
        <div className="flex items-center gap-1.5">
          {q.status === 'draft' && (
            <button
              onClick={() => handleSendQuote(q.id)}
              className="btn btn-secondary btn-sm text-xs flex items-center gap-1"
            >
              <Send className="w-3 h-3 text-[var(--gold-dark)]" />
              <span>Send</span>
            </button>
          )}
          {q.status === 'accepted' && (
            <button
              onClick={() => handleConvertQuote(q.id)}
              className="btn btn-primary btn-sm text-xs flex items-center gap-1"
            >
              <ArrowRight className="w-3 h-3" />
              <span>Convert</span>
            </button>
          )}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-serif font-bold text-[var(--text-primary)]">Custom Price Quotes</h2>
          <p className="text-xs text-[var(--text-primary)]/50 mt-1">Create custom B2B/wedding quotes with per-line discounts & conversion.</p>
        </div>
        <Link
          to="/admin/quotes/new"
          className="btn btn-primary text-xs flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          <span>Create New Quote</span>
        </Link>
      </div>

      {/* Filter Toolbar */}
      <div className="glass-card p-4">
        <div className="relative">
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
            placeholder="Search by Quote #, Customer Name, or Phone..."
            className="form-input text-xs pl-9"
          />
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-[var(--gold-dark)]" />
        </div>
      </div>

      {/* Quotes — desktop table */}
      <div className="glass-card p-4 hidden md:block">
        <AppDataTable
          columns={quoteColumns}
          data={quotes}
          keyField={(q) => q.id}
          progressPending={loading}
          noDataComponent="No custom quotes created yet."
        />
      </div>

      {/* Quotes — mobile cards */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="admin-mobile-card text-center py-8">
            <span className="spinner text-[var(--gold-dark)]" />
          </div>
        ) : quotes.length === 0 ? (
          <div className="admin-mobile-card text-center py-8 text-xs text-[var(--text-primary)]/40">
            No custom quotes created yet.
          </div>
        ) : (
          quotes.map((q) => (
            <div key={q.id} className="admin-mobile-card">
              <div className="flex justify-between items-start gap-2">
                <div>
                  <p className="font-mono text-sm font-bold text-[var(--gold-dark)]">{q.quoteNumber}</p>
                  <p className="text-sm font-medium text-[var(--text-primary)] mt-0.5">{q.customerName}</p>
                  <p className="font-mono text-[10px] text-[var(--text-muted)]">{q.customerPhone}</p>
                </div>
                <span className={`badge badge-${q.status}`}>{q.status}</span>
              </div>
              <div className="flex justify-between items-end mt-3 text-xs">
                <div>
                  <p className="admin-mobile-card-label">Date</p>
                  <p className="text-[var(--text-muted)]">{new Date(q.createdAt).toLocaleDateString('en-IN')}</p>
                </div>
                <div className="text-right">
                  <p className="admin-mobile-card-label">Total</p>
                  <p className="font-serif font-bold text-[var(--text-primary)]">{formatCurrency(q.total)}</p>
                </div>
              </div>
              {(q.status === 'draft' || q.status === 'accepted') && (
                <div className="admin-mobile-card-actions">
                  {q.status === 'draft' && (
                    <button
                      type="button"
                      onClick={() => handleSendQuote(q.id)}
                      className="flex-1 py-2 text-xs font-semibold rounded-lg border border-[var(--border)] flex items-center justify-center gap-1"
                    >
                      <Send className="w-3.5 h-3.5" /> Send
                    </button>
                  )}
                  {q.status === 'accepted' && (
                    <button
                      type="button"
                      onClick={() => handleConvertQuote(q.id)}
                      className="flex-1 py-2 text-xs font-semibold rounded-lg btn btn-primary flex items-center justify-center gap-1"
                    >
                      <ArrowRight className="w-3.5 h-3.5" /> Convert
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <DataTablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        onPageChange={setPage}
        className="pt-2"
      />
    </div>
  )
}
